import { isTruthy } from '@ardoq/common-helpers';
import {
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  ReferenceTypeNameGroupingRule,
} from '@ardoq/perspectives';
import { TextInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import { type I18nLocale, t } from '@ardoq/i18n';
import { ReferenceDirection } from '@ardoq/api-types';
import { RowHeader } from '../../atoms';
import { GroupNameHeaderInfoIcon } from '@ardoq/perspectives';

const toReferenceTypeOption = (referenceTypesName: string) => ({
  value: referenceTypesName,
  label: referenceTypesName,
});

const getReferenceDirectionOptions = (i18nLocale: I18nLocale) => [
  {
    value: ReferenceDirection.INCOMING,
    label: t(
      i18nLocale,
      'perspectivesSidebar:groupingRules.incoming',
      'Incoming'
    ),
  },
  {
    value: ReferenceDirection.OUTGOING,
    label: t(
      i18nLocale,
      'perspectivesSidebar:groupingRules.outgoing',
      'Outgoing'
    ),
  },
];

type ReferenceTypeNameGroupRuleProps = {
  groupingRule: ReferenceTypeNameGroupingRule;
  globalReferenceTypeNames: string[];
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  tabIndex?: number;
  i18nLocale?: I18nLocale;
};

const ReferenceTypeNameGroupRule = ({
  globalReferenceTypeNames,
  groupingRule,
  onGroupingRuleUpdated,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale = {},
}: ReferenceTypeNameGroupRuleProps) => {
  const { ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};
  const referenceTypeOptions = [
    ruleTargetSelectOption,
    ...globalReferenceTypeNames.map(toReferenceTypeOption),
  ].filter(isTruthy);

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <RowHeader>
        {t(i18nLocale, 'perspectivesSidebar:groupingRules.type', 'Type')}
      </RowHeader>
      <Select
        dataTestId="reference-type-name-grouping-rule-reference-types-select"
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.referenceTypeNamesAriaLabel',
          'Reference type names'
        )}
        options={referenceTypeOptions}
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event => {
          updateGroupRule({
            targetId: event?.value,
          });
        }}
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.direction',
          'Direction'
        )}
      </RowHeader>
      <Select
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.referenceDirectionAriaLabel',
          'Reference direction'
        )}
        dataTestId="reference-type-grouping-rule-direction-select"
        value={groupingRule.direction}
        options={getReferenceDirectionOptions(i18nLocale)}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            direction: event?.value ?? ReferenceDirection.INCOMING,
          })
        }
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.groupName',
          'Group name'
        )}
        <GroupNameHeaderInfoIcon />
      </RowHeader>
      <TextInput
        value={groupingRule.sublabel}
        dataTestId="reference-type-grouping-rule-sublabel-input"
        placeholder={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.addLabelPlaceholder',
          'Add a label'
        )}
        tabIndex={tabIndex}
        onValueChange={value =>
          updateGroupRule({
            sublabel: value,
          })
        }
      />
    </>
  );
};

export { ReferenceTypeNameGroupRule };
